import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { PromptTypes } from 'generated/graphql-types';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { AddPromptComponent } from '../../add-prompt/containers/add-prompt.component';
import * as AddPromptActions from '../../add-prompt/state/add-prompt.actions';
import { EditMailboxPinComponent } from '../../edit-mailbox-pin/containers/edit-mailbox-pin.component';
import { PromptOption } from '../../prompt-register/models/prompt-option.interface';
import { VoiceMailGreetingPromptOption } from '../models/voicemail-greeting-prompt-option.model';
import { GetMailboxDetail, GetMailboxDetailVariables } from '../queries/generated/GetMailboxDetail';
import { getMailboxDetail } from '../queries/mailbox-detail.graphql';
import { MailboxDetailApiService } from '../services/mailbox-detail-api.service';
import * as MailboxDetailActions from './mailbox-detail.actions';
import { getMailbox } from './mailbox-detail.selectors';
@Injectable()
export class MailboxDetailEffects {
   loadMailboxDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            MailboxDetailActions.mailboxDetailLoad,
            MailboxDetailActions.saveMailboxDetailSuccess,
            MailboxDetailActions.mailboxDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         concatMap(([action, params]) => {
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetMailboxDetail, GetMailboxDetailVariables>({
                  query: getMailboxDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([mailboxResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageExtensions_Enabled,
                           mailboxResult.data.mailbox.extension?.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return MailboxDetailActions.mailboxDetailAccessDenied();
                     }
                     return MailboxDetailActions.loadMailboxDetailSuccess({
                        data: mailboxResult.data.mailbox,
                     });
                  }),
                  catchError((error) => {
                     return of(MailboxDetailActions.loadMailboxDetailFailure(error));
                  })
               );
         })
      );
   });

   saveMailboxDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxDetailActions.saveMailboxDetail),
         switchMap((action) => {
            const apiCall = this.mailboxDetailApiService.save(action.mailbox, action.mailboxFields);
            return apiCall.pipe(
               map((result) => {
                  return MailboxDetailActions.saveMailboxDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(MailboxDetailActions.saveMailboxDetailFailure(error));
               })
            );
         })
      );
   });

   saveMailboxDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(MailboxDetailActions.saveMailboxDetailSuccess),
            tap((action) => {
               this.snackBar.open('Mailbox Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   addNewPrompt = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(MailboxDetailActions.addNewPrompt),
            withLatestFrom(this.store.pipe(select(getMailbox))),
            switchMap(([action, mailbox]) => {
               return this.openNewPromptDialog(
                  mailbox?.extension?.account.accountId,
                  action?.promptType,
                  action.voiceMailGreetingPrompt
               );
            })
         );
      },
      { dispatch: false }
   );

   addNewPromptEffect = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.createNewFromExtensionPromptSuccess),
         filter((action) => {
            return !!action?.prompt?.prompt;
         }),
         map((action) => {
            const promptOption: PromptOption = {
               ...action.prompt.prompt,
               voiceMailGreetingPrompt: action.voiceMailGreetingPrompt,
            };
            return MailboxDetailActions.promptItemDialogSubmit({ promptItem: promptOption });
         })
      );
   });

   editMailboxPin = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(MailboxDetailActions.editMailboxPin),
            switchMap((action) => {
               if (action.mailboxId) {
                  return this.openEditMailboxPinDialog(action.mailboxId);
               }
               return EMPTY;
            })
         );
      },
      { dispatch: false }
   );

   editMailboxPinSubmit$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(MailboxDetailActions.editMailboxPinSubmit),
         switchMap((action) => {
            if (!action.mailboxId) {
               return of(MailboxDetailActions.editMailboxPinFailure({ error: 'Mailbox ID is missing.' }));
            }
            return this.mailboxDetailApiService.changePin(action.mailboxId, action.password).pipe(
               map(() => MailboxDetailActions.editMailboxPinSuccess()),
               catchError((error) => of(MailboxDetailActions.editMailboxPinFailure({ error })))
            );
         })
      );
   });

   editMailboxPinSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(MailboxDetailActions.editMailboxPinSuccess),
            tap(() => {
               this.dialogService.close();
               this.snackBar.open('Mailbox PIN changed successfully', 'Close', { duration: 3000 });
            })
         );
      },
      { dispatch: false }
   );

   editMailboxPinFailure$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(MailboxDetailActions.editMailboxPinFailure),
            tap(({ error }) => this.snackBar.open(error || 'Failed to change PIN.', 'Close', { duration: 5000 }))
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private store: Store<AppState>,
      private mailboxDetailApiService: MailboxDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location,
      private dialogService: LcsDialogService
   ) {}

   openNewPromptDialog(
      accountId: number | null | undefined,
      promptType: PromptTypes,
      voiceMailGreetingPrompt: VoiceMailGreetingPromptOption | null
   ): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
         closeOnNavigation: true,
         data: {
            accountId,
            creatingNewFromExtension: true,
            promptTypeId: promptType,
            voiceMailGreetingPrompt: voiceMailGreetingPrompt,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }

   openEditMailboxPinDialog(mailboxId: number | null | undefined): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(EditMailboxPinComponent, {
         closeOnNavigation: true,
         data: {
            mailboxId,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }
}
